/**
 * Measured presentation geometry for the client-local dungeon interior.
 *
 * Scale rule:
 * - the tallest spawnable proxy is the 3.30 m sentinel;
 * - reserve two sentinel-height modules of combat clearance on each side;
 * - reserve 2.20 m of headroom above the tallest proxy.
 *
 * That produces a 16.50 m room and a 5.50 m clear wall/ceiling height.
 * Keeping the measurements and derived dimensions here prevents the assembler
 * from accumulating unrelated geometry literals.
 *
 * Under the AD-404 layout (S3) most of this is no longer global: a schema-v2
 * chamber carries its own outline and `wallHeight`, so `roomSize`, `wallHeight`
 * and `minimumCorridorLength` are read ONLY by the one-release v1 fallback. What
 * stays global — wall thickness, doorway and secret widths, the proxy-height
 * fallback table — is mirrored by the package's `DUNGEON_LAYOUT_GLOBALS`, and
 * `layoutInterior.test.ts` fails if the two copies drift.
 *
 * Wall thickness is the one global the SERVER needs too (its instance zone collides
 * against the same walls), so it is read from `@great-cloud/shared` (AD-698).
 */

import { DUNGEON_WALL_THICKNESS } from '@great-cloud/shared/client';

export const ENEMY_PROXY_HEIGHTS = Object.freeze({
  'sentinel-proxy': 3.3,
  'brute-proxy': 2.45,
  'lobber-proxy': 2.45,
  'flyer-proxy': 2.08,
  'skirmisher-proxy': 2.06,
  'swarm-proxy': 0.46,
} as const);

const largestSpawnableEnemyHeight = Math.max(...Object.values(ENEMY_PROXY_HEIGHTS));
const combatClearanceEachSide = largestSpawnableEnemyHeight * 2;
const headroomAboveLargestEnemy = 2.2;
const roomSize = largestSpawnableEnemyHeight + combatClearanceEachSide * 2;
const wallHeight = largestSpawnableEnemyHeight + headroomAboveLargestEnemy;
const wallThickness = DUNGEON_WALL_THICKNESS;
const doorwaySideClearance = 0.6;
const doorwayClearWidth = largestSpawnableEnemyHeight + doorwaySideClearance * 2;
const doorwayJambWidth = wallThickness;
const doorwayFrameOuterWidth = doorwayClearWidth + doorwayJambWidth * 2;

const encounterClearRadius = roomSize * 0.3;

const secretEntranceClearWidth = doorwayClearWidth - doorwayJambWidth * 2;
const secretEntranceOuterWidth = secretEntranceClearWidth + doorwayJambWidth * 2;
const secretPanelGapWidth = doorwaySideClearance * 2;
const secretPanelWidth = secretEntranceClearWidth - secretPanelGapWidth;
const secretAlcoveWidth = secretEntranceOuterWidth + wallThickness * 2;
const secretAlcoveDepth = largestSpawnableEnemyHeight;
const secretPanelRecess = wallThickness * 0.2;
const secretDressingMaximumFootprint = largestSpawnableEnemyHeight * 0.4;
const secretDressingClearance = 0.35;
const secretRubbleMaximumFootprint = wallThickness * 0.8;

export const DUNGEON_SECRET_AREA_SCALE = Object.freeze({
  secretEntranceClearWidth,
  secretEntranceOuterWidth,
  secretPanelGapWidth,
  secretPanelWidth,
  secretAlcoveWidth,
  secretAlcoveDepth,
  secretPanelRecess,
  secretDressingMaximumFootprint,
  secretDressingClearance,
  secretRubbleMaximumFootprint,
});

export const DUNGEON_INTERIOR_SCALE = Object.freeze({
  largestSpawnableEnemyHeight,
  combatClearanceEachSide,
  headroomAboveLargestEnemy,
  roomSize,
  wallHeight,

  floorThickness: 0.2,
  ceilingThickness: 0.2,
  wallThickness,
  minimumWallTileSpan: largestSpawnableEnemyHeight * 0.75,
  maximumWallTileSpan: largestSpawnableEnemyHeight,

  doorwaySideClearance,
  doorwayClearWidth,
  doorwayClearHeight: largestSpawnableEnemyHeight + 0.9,
  doorwayJambWidth,
  doorwayFrameOuterWidth,
  thresholdHeight: 0.08,

  minimumCorridorLength: combatClearanceEachSide,

  navigationCoreRadius: largestSpawnableEnemyHeight * 0.9,
  encounterClearRadius,
  encounterProxySpacing: largestSpawnableEnemyHeight * 0.8,
  encounterFormationSpan: encounterClearRadius * 1.25,

  maximumDressingFootprint: largestSpawnableEnemyHeight * 0.8,
  dressingWallClearance: 0.55,
  dressingSpacing: 0.35,
  dressingRingSpacing: 1.1,
  dressingCandidateRingCount: 4,
  dressingCandidatesPerRing: 48,
  wallMountBaseHeight: largestSpawnableEnemyHeight * 0.7,
});
